import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Star, Minus, Plus, Lock, Truck, RefreshCcw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONAS = ["Devoted", "Playful", "Confident", "Gentle"];

const SPEC_LABELS = {
  frame: "Skeleton",
  height: "Height",
  skin_temp: "Skin",
  neural_engine: "AI / Voice",
  battery: "Power",
  weight: "Weight",
};

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [persona, setPersona] = useState("Devoted");
  const { addItem } = useCart();

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    axios
      .get(`${API}/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  const handleAdd = () => {
    addItem(product, qty);
    toast.success(`${product.name} added to your cart`, {
      description: `Persona preset: ${persona}. Discreet packaging included.`,
    });
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 pt-16" data-testid="product-not-found">
        <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-slate-500">
          Unit not found
        </p>
        <Link to="/" className="text-magma font-display font-bold uppercase tracking-widest text-sm border-b border-magma pb-1">
          Return to collection
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16" data-testid="product-loading">
        <p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-slate-500">
          Initializing unit…
        </p>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-view" className="pt-16 min-h-screen">
      <div className="px-4 sm:px-8 lg:px-16 py-8">
        <Link
          to="/"
          data-testid="back-to-collection-link"
          className="inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.25em] text-slate-400 hover:text-cyber transition-colors"
        >
          <ArrowLeft size={14} />
          Collection
        </Link>
      </div>

      <div className="px-4 sm:px-8 lg:px-16 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          className="lg:col-span-6 relative"
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="absolute -inset-8 bg-[radial-gradient(circle_at_center,rgba(255,42,109,0.15),transparent_65%)] blur-2xl" />
          <div className="relative clip-frame overflow-hidden border border-white/10">
            <img
              src={product.image}
              alt={`Aetheria ${product.name} companion`}
              className="w-full h-[65vh] object-cover"
            />
            <div className="scanlines" />
            <div className="absolute inset-0 bg-gradient-to-t from-void/80 via-transparent to-transparent" />
            <span className="absolute top-5 left-5 font-mono-tech text-[10px] uppercase tracking-[0.25em] bg-black/70 backdrop-blur-md border border-magma/40 text-magma px-3 py-1.5">
              {product.badge}
            </span>
          </div>
        </motion.div>

        <motion.div
          className="lg:col-span-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono-tech text-xs uppercase tracking-[0.35em] text-cyber mb-3">
            {product.gender} unit // {product.slug}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-none mb-4">
            {product.name}
          </h1>
          <div className="flex items-center gap-4 mb-6">
            <span className="flex items-center gap-1.5 font-mono-tech text-xs text-cyber">
              <Star size={12} fill="currentColor" />
              {product.rating} / {product.reviews} reviews
            </span>
            <span className="flex items-baseline gap-3">
              {product.compare_at && (
                <span className="font-mono-tech text-sm text-slate-500 line-through">
                  {product.compare_at}
                </span>
              )}
              <span className="font-display font-black text-2xl text-magma" data-testid="product-price">
                {product.price}
              </span>
            </span>
          </div>
          <p className="text-base text-slate-400 leading-relaxed mb-10 max-w-xl">
            {product.description}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/8 border border-white/8 mb-10" data-testid="product-specs-grid">
            {Object.entries(product.specs).map(([k, v]) => (
              <div key={k} className="bg-panel p-4">
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                  {SPEC_LABELS[k] || k}
                </p>
                <p className="font-display font-bold text-sm text-white">{v}</p>
              </div>
            ))}
          </div>

          <p className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">
            Persona preset
          </p>
          <div className="flex flex-wrap gap-2 mb-10" data-testid="persona-selector">
            {PERSONAS.map((p) => (
              <button
                key={p}
                data-testid={`persona-${p.toLowerCase()}-btn`}
                onClick={() => setPersona(p)}
                className={`font-mono-tech text-xs uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors duration-300 ${
                  persona === p
                    ? "border-cyber text-cyber bg-cyber/10"
                    : "border-white/12 text-slate-400 hover:border-white/35 hover:text-white"
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <div className="flex items-stretch gap-4 mb-8">
            <div className="flex items-center border border-white/15">
              <button
                data-testid="qty-minus-btn"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-11 h-full flex items-center justify-center hover:text-magma transition-colors"
                aria-label="Decrease quantity"
              >
                <Minus size={14} />
              </button>
              <span className="w-10 text-center font-mono-tech" data-testid="qty-value">{qty}</span>
              <button
                data-testid="qty-plus-btn"
                onClick={() => setQty((q) => Math.min(5, q + 1))}
                className="w-11 h-full flex items-center justify-center hover:text-magma transition-colors"
                aria-label="Increase quantity"
              >
                <Plus size={14} />
              </button>
            </div>
            <button
              data-testid="add-to-cart-btn"
              onClick={handleAdd}
              className="flex-1 bg-magma text-white font-display font-bold uppercase tracking-widest text-sm py-4 glow-magenta hover:bg-[#ff4d87] transition-colors duration-300"
            >
              Add to Cart — {product.price}
            </button>
          </div>

          <div className="space-y-3 border-t border-white/8 pt-6">
            {[
              { icon: Lock, text: "Neutral billing descriptor. Zero data retention." },
              { icon: Truck, text: "White-glove delivery in an unmarked crate, 4–6 weeks." },
              { icon: RefreshCcw, text: "30-day alignment guarantee. 5-year frame warranty." },
            ].map((row, i) => (
              <div key={i} className="flex items-center gap-3 text-slate-500">
                <row.icon size={14} className="text-cyber shrink-0" />
                <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em]">{row.text}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
